import React, { useState } from 'react';
import { CheckCircle, Loader2, MessageCircle, Search, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const premiumBenefits = [
  {
    icon: MessageCircle,
    title: 'Conversa via WhatsApp',
    description: 'Mostre o botão direto no perfil público para o paciente chamar sua equipe com menos atrito.',
  },
  {
    icon: TrendingUp,
    title: 'Destaque no BuscaDoutor',
    description: 'Ganhe mais visibilidade nas páginas de busca e nos pontos de decisão do paciente.',
  },
  {
    icon: Search,
    title: 'SEO especial para Google',
    description: 'Perfil mais completo, organizado e preparado para melhorar a descoberta orgânica.',
  },
];

const planHighlights = [
  'WhatsApp liberado no perfil público',
  'Destaque nas buscas do BuscaDoutor',
  'SEO especial para Google',
  'Relatório mensal de visibilidade',
];

const checkoutPlans = [
  {
    id: 'monthly',
    title: 'Mensal recorrente',
    price: 'R$ 197',
    period: 'por mês',
    badge: null,
  },
  {
    id: 'annual',
    title: 'Anual recorrente',
    price: 'R$ 1.891,20',
    period: 'por ano',
    badge: '20% de desconto',
  },
];

export default function PremiumUpgradeCard({
  isPremium = false,
  requestStatus,
  onRequestUpgrade,
  onCheckout,
}) {
  const [submittingPlan, setSubmittingPlan] = useState('');
  const [localStatus, setLocalStatus] = useState(requestStatus || '');
  const checkoutHandler = onCheckout || onRequestUpgrade;
  const hasPreviousRequest = ['pending', 'contacted'].includes(String(localStatus || requestStatus || '').toLowerCase());

  const handleCheckout = async (plan) => {
    if (!checkoutHandler) return;
    setSubmittingPlan(plan);
    try {
      const result = await checkoutHandler(plan);
      setLocalStatus(result?.status || '');
    } catch {
      // The page-level handler already shows a toast; keep the card mounted.
    } finally {
      setSubmittingPlan('');
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-5 shadow-sm" data-testid="premium-upgrade-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Premium</p>
            <h2 className="text-lg font-bold text-slate-950">Mais pacientes vendo seu perfil</h2>
          </div>
        </div>
        {isPremium ? (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ativo</Badge>
        ) : hasPreviousRequest ? (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Checkout disponível</Badge>
        ) : null}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        O plano Premium transforma seu perfil em uma página mais visível, acionável e preparada para conversão.
      </p>

      <div className="mt-4 rounded-xl border border-amber-200 bg-white/90 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-950">Premium BuscaDoutor</p>
            <p className="mt-1 text-xs text-slate-500">Para profissionais e clínicas que querem converter mais pacientes.</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-950">R$ 197</p>
            <p className="text-xs text-slate-500">por mês</p>
          </div>
        </div>
        <div className="mt-3 grid gap-2">
          {planHighlights.map((item) => (
            <div key={item} className="flex items-center gap-2 text-xs text-slate-700">
              <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {premiumBenefits.map((benefit) => {
          const Icon = benefit.icon;
          return (
            <div key={benefit.title} className="flex gap-3 rounded-xl border border-white/80 bg-white/80 p-3">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{benefit.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{benefit.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {isPremium ? (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Seu perfil já está Premium. Mantenha os dados completos para aproveitar melhor a visibilidade.</span>
        </div>
      ) : checkoutHandler ? (
        <div className="mt-5 grid gap-3">
          {checkoutPlans.map((plan) => (
            <Button
              key={plan.id}
              type="button"
              onClick={() => handleCheckout(plan.id)}
              disabled={!!submittingPlan}
              className="h-auto w-full justify-between bg-amber-400 px-4 py-3 text-left text-slate-950 shadow-none hover:bg-amber-300"
              data-testid={`premium-${plan.id}-checkout-button`}
            >
              <span className="flex items-center gap-2">
                {submittingPlan === plan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span>
                  <span className="block font-semibold">{plan.title}</span>
                  {plan.badge && <span className="block text-xs font-medium text-emerald-900">{plan.badge}</span>}
                </span>
              </span>
              <span className="text-right">
                <span className="block font-bold">{plan.price}</span>
                <span className="block text-xs font-medium">{plan.period}</span>
              </span>
            </Button>
          ))}
        </div>
      ) : (
        <Button
          type="button"
          disabled
          className="mt-5 w-full bg-slate-200 text-slate-500 shadow-none"
          data-testid="premium-upgrade-button"
        >
          Checkout indisponível
        </Button>
      )}
    </div>
  );
}
